import { bcPhoneMessages } from '@/constants/errorMessages';
import {
  BcAttemptState,
  PhoneVerificationError,
  type ScoringState,
} from '../types/scoring';

export const ScoringStep = {
  // Call initiate-score-obtaining (first time, or KYC now valid)
  Start: 'start',
  PhoneVerification: 'phone-verification',
  Done: 'done',
} as const;

export type ScoringStep = (typeof ScoringStep)[keyof typeof ScoringStep];

/**
 * Where the user is in obtaining a BC score. `Done` does not mean a usable
 * score exists: see `hasBcScore` for whether a preliminary offer is possible.
 * An outdated score is still done: initiate-score-obtaining never scores
 * again once a score exists. The offer request (with KYC) and the
 * preliminary offer (without) ask BC for a fresh one themselves.
 */
export const getScoringStep = (
  state: ScoringState | undefined,
  isKycValid: boolean
): ScoringStep => {
  if (!state) {
    return ScoringStep.Start;
  }

  switch (state.bcAttemptState) {
    case BcAttemptState.Completed:
    case BcAttemptState.CnpNotFound:
      return ScoringStep.Done;
    case BcAttemptState.NeedPhoneVerification:
      // With KYC the backend scores directly, no phone confirmation needed.
      return isKycValid ? ScoringStep.Start : ScoringStep.PhoneVerification;
    case BcAttemptState.NoPhoneRegistered:
      // Without KYC there is no way to score; the user continues to KYC.
      return isKycValid ? ScoringStep.Start : ScoringStep.Done;
    default:
      return ScoringStep.Start;
  }
};

// Completed means the score is requested or ready; CnpNotFound and
// NoPhoneRegistered leave the user without a score to base an offer on.
export const hasBcScore = (state: ScoringState | undefined): boolean =>
  state?.bcAttemptState === BcAttemptState.Completed;

const phoneVerificationErrorMessages: Record<PhoneVerificationError, string> = {
  [PhoneVerificationError.None]: bcPhoneMessages.unexpected,
  [PhoneVerificationError.InvalidCode]: bcPhoneMessages.invalid_code,
  [PhoneVerificationError.LimitExceeded]: bcPhoneMessages.limit_exceeded,
  [PhoneVerificationError.Unexpected]: bcPhoneMessages.unexpected,
};

// Why a BC SMS code was refused: wrong or expired codes answer HTTP 200.
export const getPhoneVerificationErrorMessage = (
  error: PhoneVerificationError
): string => phoneVerificationErrorMessages[error] ?? bcPhoneMessages.unexpected;
